/* ==========================================================================
   SINAÍ BARCELONA — homepage behaviour
   Deliberately small. The reference site carries no scroll-reveal animation
   at all, so the only motion here either answers a user action or is the
   marquee. Everything re-initialises on Shopify's theme-editor events.
   ========================================================================== */

(function () {
  'use strict';

  /* ---------------------------------------------------------------- drawer */

  function initDrawer(root) {
    var drawer = root.querySelector('[data-sinai-drawer]');
    if (!drawer || drawer.dataset.sinaiReady === '1') return;
    drawer.dataset.sinaiReady = '1';

    var openers = root.querySelectorAll('[data-sinai-drawer-open]');
    // Scoped to the section, not the drawer: the scrim is a sibling of the
    // panel, so a drawer-scoped query would miss it.
    var closers = root.querySelectorAll('[data-sinai-drawer-close]');
    var lastFocus = null;

    function setOpen(open) {
      drawer.setAttribute('data-open', String(open));
      document.body.classList.toggle('sinai-no-scroll', open);
      openers.forEach(function (b) { b.setAttribute('aria-expanded', String(open)); });
      if (open) {
        lastFocus = document.activeElement;
        var first = drawer.querySelector('button, a');
        if (first) first.focus();
      } else if (lastFocus && lastFocus.focus) {
        lastFocus.focus();
      }
    }

    openers.forEach(function (b) { b.addEventListener('click', function () { setOpen(true); }); });
    closers.forEach(function (b) { b.addEventListener('click', function () { setOpen(false); }); });

    drawer.addEventListener('click', function (e) {
      if (e.target.closest('a')) setOpen(false);
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && drawer.getAttribute('data-open') === 'true') setOpen(false);
    });

    // Keep focus inside the drawer while it is open.
    drawer.addEventListener('keydown', function (e) {
      if (e.key !== 'Tab' || drawer.getAttribute('data-open') !== 'true') return;
      var items = drawer.querySelectorAll('a[href], button:not([disabled])');
      if (!items.length) return;
      var first = items[0];
      var last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });
  }

  /* --------------------------------------------------------------- marquee */

  function initMarquee(root) {
    root.querySelectorAll('[data-sinai-marquee]').forEach(function (el) {
      var track = el.querySelector('.sinai-marquee__track');
      if (!track) return;

      // Rebuild from the pristine first child every time, so repeated editor
      // renders don't stack clones on top of clones.
      if (!track.dataset.sinaiSeed) {
        track.dataset.sinaiSeed = track.innerHTML;
      }
      track.innerHTML = track.dataset.sinaiSeed;

      var seed = track.innerHTML;
      var guard = 0;
      // Repeat until one half comfortably exceeds the viewport, otherwise a
      // short phrase leaves a visible gap before the loop point.
      while (track.scrollWidth < el.clientWidth * 2 && guard < 12) {
        track.innerHTML += seed;
        guard++;
      }
      // Duplicate the whole run once; the -50% keyframe then lands exactly on
      // the seam, which is what makes the loop invisible.
      track.innerHTML += track.innerHTML;
    });
  }

  /* -------------------------------------------------------------- carousel */

  function initCarousel(root) {
    root.querySelectorAll('[data-sinai-carousel]').forEach(function (wrap) {
      var viewport = wrap.querySelector('[data-sinai-carousel-track]');
      var prev = wrap.querySelector('[data-sinai-prev]');
      var next = wrap.querySelector('[data-sinai-next]');
      if (!viewport || !prev || !next) return;

      function step() {
        var card = viewport.firstElementChild;
        if (!card) return viewport.clientWidth;
        var styles = getComputedStyle(viewport);
        var gap = parseFloat(styles.columnGap || styles.gap) || 0;
        return card.getBoundingClientRect().width + gap;
      }

      function sync() {
        var max = viewport.scrollWidth - viewport.clientWidth - 2;
        var scrollable = max > 0;
        wrap.classList.toggle('sinai-hide', false);
        prev.disabled = !scrollable || viewport.scrollLeft <= 2;
        next.disabled = !scrollable || viewport.scrollLeft >= max;
        // Hide the control row entirely when nothing can scroll.
        var nav = wrap.querySelector('.sinai-nav');
        if (nav) nav.style.display = scrollable ? '' : 'none';
      }

      prev.addEventListener('click', function () { viewport.scrollBy({ left: -step(), behavior: 'smooth' }); });
      next.addEventListener('click', function () { viewport.scrollBy({ left: step(), behavior: 'smooth' }); });
      viewport.addEventListener('scroll', sync, { passive: true });
      window.addEventListener('resize', sync);
      sync();
    });
  }

  /* ---------------------------------------------------------- hero slider */

  /* ------------------------------------------------------------- reveals */

  /* One quiet entrance per section: copy rises, editorial images arrive from
     alternating sides, product cards land in sequence. Everything is additive -
     the hidden state is only ever applied from JavaScript, so with it off the
     page renders exactly as before. */
  function initReveal(root) {
    /* Order matters. Specific entries claim their elements first; the catch-all
       afterwards skips anything sitting inside - or around - something already
       claimed, so nothing animates twice. */
    var TARGETS = [
      { sel: '.sinai-card', mode: 'up', stagger: 60 },
      { sel: '.sinai-pillars__list > *', mode: 'up', stagger: 70 },
      { sel: '.sinai-imgtext__media', mode: 'wipeAlternate' },
      { sel: '.sinai-imgtext__body', mode: 'up', delay: 140 },
      { sel: '.sinai-faq__head', mode: 'up' },
      { sel: '.sinai-faq__list', mode: 'up', delay: 90 },
      { sel: '.sinai-collection__head', mode: 'up' },
      { sel: '.sinai-section:not(.sinai-hero) > .sinai-wrap > *', mode: 'up' }
    ];

    /* Four steps is plenty to read as a sequence. Past that a grid just feels
       slow, which is the opposite of the point. */
    var STAGGER_CAP = 4;

    var marked = [];
    var counters = {};

    function claim(el, mode, delay) {
      if (el.hasAttribute('data-sinai-rv')) return;
      // The marquee animates itself; the hero is above the fold and must not wait.
      if (el.closest('.sinai-hero, .sinai-marquee, .sinai-header, .sinai-footer, dialog')) return;
      for (var i = 0; i < marked.length; i++) {
        if (marked[i].contains(el) || el.contains(marked[i])) return;
      }
      el.setAttribute('data-sinai-rv', mode);
      if (delay) el.style.setProperty('--sinai-rv-delay', delay + 'ms');
      marked.push(el);
    }

    TARGETS.forEach(function (t, ti) {
      var nodes = [].slice.call(root.querySelectorAll(t.sel));
      nodes.forEach(function (el, n) {
        var mode = t.mode;
        var delay = t.delay || 0;

        if (mode === 'alternate') {
          // Images come in from opposite sides down the page, so two stacked
          // image/text bands never repeat the same move.
          mode = (n % 2 === 0) ? 'left' : 'right';
        }

        if (mode === 'wipeAlternate') {
          /* Editorial images get the uncover rather than the nudge: the frame
             opens from one edge while the photo drifts the other way inside it.
             Alternating sides stops two stacked bands repeating the same move. */
          mode = (n % 2 === 0) ? 'wipe-left' : 'wipe-right';
        }

        if (t.stagger) {
          /* Count within the parent, not the page: every grid restarts its own
             sequence instead of inheriting the offset of the one above it. */
          var parent = el.parentElement;
          if (!parent.__rvKey) parent.__rvKey = 'g' + ti + '_' + Math.random().toString(36).slice(2, 7);
          var key = parent.__rvKey;
          counters[key] = (counters[key] || 0);
          delay = Math.min(counters[key], STAGGER_CAP) * t.stagger;
          counters[key]++;
        }

        claim(el, mode, delay);
      });
    });

    if (!marked.length) return;

    /* A plain scroll check rather than IntersectionObserver. Above 990px the
       page scrolls inside .page-wrapper, not the window, and an observer bound
       to the viewport proved unreliable there - it left clipped images hidden.
       Reading rects for a handful of elements costs nothing. */
    var queued = false;

    function check() {
      queued = false;
      var vh = window.innerHeight || document.documentElement.clientHeight;
      for (var i = 0; i < marked.length; i++) {
        var el = marked[i];
        var r = el.getBoundingClientRect();
        /* Show as soon as it is meaningfully on screen; hide again only once it
           is well clear of both edges. The gap between the two thresholds is
           what stops an element flickering while it sits on the boundary. */
        if (r.top < vh - 40 && r.bottom > 40) {
          el.classList.add('is-in');
        } else if (r.bottom < -80 || r.top > vh + 80) {
          el.classList.remove('is-in');
        }
      }
    }

    /* setTimeout rather than requestAnimationFrame on purpose: rAF is paused in
       background tabs and throttled by some embedded views, which would leave a
       clipped image invisible until the tab regained focus. A timer always runs. */
    function onScroll() {
      if (queued) return;
      queued = true;
      setTimeout(check, 60);
    }

    // First pass is synchronous: whatever is already painted appears at once,
    // so the opening screen never flashes from empty to full.
    var vh0 = window.innerHeight || document.documentElement.clientHeight;
    marked.forEach(function (el) {
      var r = el.getBoundingClientRect();
      if (r.top < vh0 && r.bottom > 0) {
        el.classList.add('is-instant', 'is-in');
        requestAnimationFrame(function () {
          requestAnimationFrame(function () { el.classList.remove('is-instant'); });
        });
      }
    });

    /* `capture: true` is what lets a scroll inside .page-wrapper reach us:
       scroll events do not bubble, but they do pass through the capture phase.
       The listener stays for the life of the page - reveals replay on the way
       back up, so there is nothing to tear down. */
    document.addEventListener('scroll', onScroll, { passive: true, capture: true });
    window.addEventListener('resize', onScroll);
    window.addEventListener('load', onScroll);
    // Late layout (fonts, lazy images) can move things into view without a scroll.
    setTimeout(check, 400);
    setTimeout(check, 1500);
  }

  function initSlider(root) {
    root.querySelectorAll('[data-sinai-slider]').forEach(function (el) {
      if (el.dataset.sinaiReady === '1') return;
      el.dataset.sinaiReady = '1';

      var track = el.querySelector('[data-slider-track]');
      var slides = track ? [].slice.call(track.querySelectorAll('[data-slide]')) : [];
      if (!track || slides.length < 2) return;

      var dots = [].slice.call(el.querySelectorAll('[data-slider-dot]'));
      var prev = el.querySelector('[data-slider-prev]');
      var next = el.querySelector('[data-slider-next]');
      var timer = null;
      var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      /* Crossfade stacks the slides instead of scrolling them, so the active
         index has to be tracked here - there is no scrollLeft to read back. */
      var fade = el.dataset.transition === 'fade';
      var index = 0;

      function current() {
        return fade ? index : Math.round(track.scrollLeft / track.clientWidth);
      }

      function goTo(i, smooth) {
        var idx = (i + slides.length) % slides.length;
        if (fade) {
          index = idx;
          slides.forEach(function (s, n) { s.classList.toggle('is-active', n === idx); });
          sync();
          return;
        }
        track.scrollTo({ left: idx * track.clientWidth, behavior: smooth === false || reduced ? 'auto' : 'smooth' });
      }

      function sync() {
        var i = current();
        dots.forEach(function (d, n) { d.setAttribute('aria-selected', String(n === i)); });
        // Slides out of view must not be reachable by keyboard.
        slides.forEach(function (s, n) {
          s.toggleAttribute('inert', n !== i);
          s.setAttribute('aria-hidden', String(n !== i));
        });
      }

      function start() {
        if (reduced || el.dataset.autoplay !== 'true') return;
        if (slides.length < 2) return;
        stop();
        var every = (parseInt(el.dataset.interval, 10) || 6) * 1000;
        timer = setInterval(function () { goTo(current() + 1); }, every);
      }
      function stop() { if (timer) { clearInterval(timer); timer = null; } }

      if (prev) prev.addEventListener('click', function () { goTo(current() - 1); stop(); start(); });
      if (next) next.addEventListener('click', function () { goTo(current() + 1); stop(); start(); });
      dots.forEach(function (d) {
        d.addEventListener('click', function () { goTo(parseInt(d.dataset.sliderDot, 10)); stop(); start(); });
      });

      track.addEventListener('scroll', function () {
        clearTimeout(track.__t);
        track.__t = setTimeout(sync, 90);
      }, { passive: true });

      /* Pause-on-hover only where a real pointer exists. Touch screens
         synthesise `mouseenter` on tap but never fire `mouseleave`, which left
         autoplay stopped for good after the first touch. */
      if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
        el.addEventListener('mouseenter', stop);
        el.addEventListener('mouseleave', start);
      }
      el.addEventListener('focusin', stop);
      el.addEventListener('focusout', start);
      document.addEventListener('visibilitychange', function () {
        if (document.hidden) stop(); else start();
      });

      // Keyboard support on the track itself.
      track.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowRight') { e.preventDefault(); goTo(current() + 1); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); goTo(current() - 1); }
      });

      // Width changes invalidate scrollLeft, so re-anchor to the active slide.
      var last = track.clientWidth;
      window.addEventListener('resize', function () {
        // Stacked slides do not depend on width, so there is nothing to re-anchor.
        if (fade) return;
        if (track.clientWidth === last) return;
        var i = Math.round(track.scrollLeft / last);
        last = track.clientWidth;
        goTo(i, false);
      });

      sync();
      start();
    });
  }

  /* ------------------------------------------------------ header dropdown */

  function initDropdowns(root) {
    root.querySelectorAll('[data-sinai-hasmenu]').forEach(function (item) {
      if (item.dataset.sinaiReady === '1') return;
      item.dataset.sinaiReady = '1';

      var trigger = item.querySelector('[data-sinai-menutrigger]');
      var panel = item.querySelector('[data-sinai-submenu]');
      if (!trigger || !panel) return;

      function setOpen(state) {
        item.setAttribute('data-open', String(state));
        trigger.setAttribute('aria-expanded', String(state));
      }

      /* Click-to-toggle only, deliberately.
         A hover-to-open menu fights its own click handler: the pointer opens it
         on mouseenter, then the click immediately toggles it shut, so the menu
         reads as broken. Click alone is predictable and works for mouse, touch
         and keyboard with one code path. */
      trigger.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        var willOpen = item.getAttribute('data-open') !== 'true';
        // Only one menu open at a time.
        document.querySelectorAll('[data-sinai-hasmenu][data-open="true"]').forEach(function (other) {
          if (other !== item) {
            other.setAttribute('data-open', 'false');
            var t = other.querySelector('[data-sinai-menutrigger]');
            if (t) t.setAttribute('aria-expanded', 'false');
          }
        });
        setOpen(willOpen);
      });

      // Click anywhere else closes it.
      document.addEventListener('click', function (e) {
        if (!item.contains(e.target)) setOpen(false);
      });

      item.addEventListener('focusout', function (e) {
        if (!item.contains(e.relatedTarget)) setOpen(false);
      });

      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && item.getAttribute('data-open') === 'true') {
          setOpen(false);
          trigger.focus();
        }
      });
    });

    /* Mobile drawer: expandable groups */
    root.querySelectorAll('[data-sinai-drawer-toggle]').forEach(function (btn) {
      if (btn.dataset.sinaiReady === '1') return;
      btn.dataset.sinaiReady = '1';
      btn.addEventListener('click', function () {
        var group = btn.closest('[data-sinai-drawer-group]');
        if (!group) return;
        var open = group.getAttribute('data-open') === 'true';
        group.setAttribute('data-open', String(!open));
        btn.setAttribute('aria-expanded', String(!open));
      });
    });
  }

  /* ----------------------------------------------------------- product page */

  function initPdp(root) {
    root.querySelectorAll('[data-sinai-pdp]').forEach(function (pdp) {
      if (pdp.dataset.sinaiReady === '1') return;
      pdp.dataset.sinaiReady = '1';

      /* --- gallery: stage <-> thumbs/dots stay in sync --- */
      var stage = pdp.querySelector('[data-sinai-pdp-stage]');
      var thumbs = [].slice.call(pdp.querySelectorAll('[data-sinai-pdp-thumb]'));
      var dots = [].slice.call(pdp.querySelectorAll('[data-sinai-pdp-dot]'));

      if (stage) {
        var slides = [].slice.call(stage.querySelectorAll('[data-sinai-pdp-slide]'));

        var goTo = function (i) {
          stage.scrollTo({ left: i * stage.clientWidth, behavior: 'smooth' });
        };
        var rail = pdp.querySelector('[data-sinai-pdp-thumbs]');

        /* Keep the active thumbnail in view as the shopper swipes the stage.
           It only scrolls when the thumbnail is actually outside the rail, so a
           rail that already shows everything never twitches. The rail's own
           scroll position is set rather than scrollIntoView, which would also
           move the page. Handles the row on mobile and the column on desktop. */
        var keepThumbInView = function (i) {
          if (!rail) return;
          var thumb = thumbs[i];
          if (!thumb) return;

          var railBox = rail.getBoundingClientRect();
          var box = thumb.getBoundingClientRect();
          var pad = 12;
          var smooth = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';

          if (rail.scrollWidth > rail.clientWidth + 1) {
            if (box.left < railBox.left) rail.scrollBy({ left: box.left - railBox.left - pad, behavior: smooth });
            else if (box.right > railBox.right) rail.scrollBy({ left: box.right - railBox.right + pad, behavior: smooth });
          }
          if (rail.scrollHeight > rail.clientHeight + 1) {
            if (box.top < railBox.top) rail.scrollBy({ top: box.top - railBox.top - pad, behavior: smooth });
            else if (box.bottom > railBox.bottom) rail.scrollBy({ top: box.bottom - railBox.bottom + pad, behavior: smooth });
          }
        };

        var mark = function (i) {
          thumbs.forEach(function (t, n) { t.setAttribute('aria-current', String(n === i)); });
          dots.forEach(function (d, n) { d.setAttribute('aria-current', String(n === i)); });
          keepThumbInView(i);
        };

        thumbs.forEach(function (t) {
          t.addEventListener('click', function () { goTo(parseInt(t.dataset.sinaiPdpThumb, 10)); });
        });
        dots.forEach(function (d) {
          d.addEventListener('click', function () { goTo(parseInt(d.dataset.sinaiPdpDot, 10)); });
        });

        stage.addEventListener('scroll', function () {
          clearTimeout(stage.__t);
          stage.__t = setTimeout(function () {
            if (!stage.clientWidth) return;
            mark(Math.round(stage.scrollLeft / stage.clientWidth));
          }, 80);
        }, { passive: true });

        // Re-anchor after a width change, otherwise scrollLeft points nowhere.
        var lastW = stage.clientWidth;
        window.addEventListener('resize', function () {
          if (stage.clientWidth === lastW || !lastW) return;
          var i = Math.round(stage.scrollLeft / lastW);
          lastW = stage.clientWidth;
          stage.scrollTo({ left: i * lastW, behavior: 'auto' });
        });

        if (slides.length) mark(0);
      }

      /* --- sticky info column ---
         A sticky element taller than the viewport pins its top, so its own
         bottom never scrolls into view. On a short laptop that hid the buy
         button entirely. Release it to normal flow whenever it does not fit. */
      var info = pdp.querySelector('.sinai-pdp__info--sticky');
      if (info) {
        var fitSticky = function () {
          info.classList.remove('is-tall');
          if (info.getBoundingClientRect().height > window.innerHeight - 48) {
            info.classList.add('is-tall');
          }
        };
        fitSticky();
        window.addEventListener('resize', fitSticky);
        window.addEventListener('load', fitSticky);
        /* Mobile browsers change the visible height when the URL bar collapses
           without always firing a window resize. */
        if (window.visualViewport) window.visualViewport.addEventListener('resize', fitSticky);
        pdp.querySelectorAll('.sinai-pdp__acc').forEach(function (d) {
          d.addEventListener('toggle', fitSticky);
        });
        if (document.fonts && document.fonts.ready) document.fonts.ready.then(fitSticky);
        /* The first measurement can land before the webfonts reflow the column,
           which left it marked as fitting when it no longer did. Watching the
           box catches every later change. Toggling the class only switches
           `position`, which does not alter the height, so this cannot loop. */
        if (window.ResizeObserver) new ResizeObserver(fitSticky).observe(info);
      }

      /* --- quantity stepper --- */
      var qty = pdp.querySelector('[data-sinai-qty]');
      if (qty) {
        var input = qty.querySelector('[data-sinai-qty-input]');
        var dec = qty.querySelector('[data-sinai-qty-down]');
        var inc = qty.querySelector('[data-sinai-qty-up]');

        var sync = function () {
          var v = parseInt(input.value, 10);
          if (isNaN(v) || v < 1) v = 1;
          input.value = v;
          if (dec) dec.disabled = v <= 1;
        };
        if (dec) dec.addEventListener('click', function () { input.value = Math.max(1, (parseInt(input.value, 10) || 1) - 1); sync(); });
        if (inc) inc.addEventListener('click', function () { input.value = (parseInt(input.value, 10) || 1) + 1; sync(); });
        input.addEventListener('change', sync);
        input.addEventListener('blur', sync);
        sync();
      }

      /* --- variant picker ---
         The variant JSON is rendered inside this section, so a page can never
         pick up another product's variants. */
      var dataEl = pdp.querySelector('[data-sinai-variants]');
      var picker = pdp.querySelector('[data-sinai-variant-picker]');
      if (!dataEl || !picker) return;

      var variants;
      try { variants = JSON.parse(dataEl.textContent); } catch (e) { return; }

      var idInput = pdp.querySelector('[data-sinai-variant-id]');
      var priceValue = pdp.querySelector('[data-sinai-price-value]');
      var atc = pdp.querySelector('[data-sinai-atc]');
      var groups = [].slice.call(picker.querySelectorAll('.sinai-pdp__opts'));

      function selected() {
        return groups.map(function (g) {
          var on = g.querySelector('[aria-pressed="true"]');
          return on ? on.dataset.value : null;
        });
      }

      /* The CSS already styles [data-unavailable], but nothing was setting it,
         so an unbuyable combination looked identical to a buyable one. */
      function markAvailability() {
        var want = selected();
        groups.forEach(function (g, gi) {
          g.querySelectorAll('[data-sinai-opt]').forEach(function (btn) {
            var probe = want.slice();
            probe[gi] = btn.dataset.value;
            var any = variants.some(function (v) {
              return v.available && probe.every(function (val, i) {
                return val === null || v.options[i] === val;
              });
            });
            btn.setAttribute('data-unavailable', String(!any));
          });
        });
      }

      function apply() {
        var want = selected();
        var match = variants.find(function (v) {
          return want.every(function (val, i) { return val === null || v.options[i] === val; });
        });
        if (!match) return;

        if (idInput) idInput.value = match.id;
        if (priceValue) priceValue.textContent = match.priceFormatted;

        if (atc) {
          atc.disabled = !match.available;
          atc.textContent = match.available ? (atc.dataset.labelAvailable || atc.textContent) : (atc.dataset.labelSoldOut || atc.textContent);
        }

        picker.querySelectorAll('[data-sinai-optlabel]').forEach(function (el) {
          var i = parseInt(el.dataset.sinaiOptlabel, 10);
          if (want[i]) el.textContent = want[i];
        });

        // Keep the URL shareable without adding a history entry per click.
        try {
          var url = new URL(window.location.href);
          url.searchParams.set('variant', match.id);
          window.history.replaceState({}, '', url);
        } catch (e) {}

        markAvailability();
      }

      markAvailability();

      groups.forEach(function (g) {
        g.addEventListener('click', function (e) {
          var btn = e.target.closest('[data-sinai-opt]');
          if (!btn) return;
          g.querySelectorAll('[data-sinai-opt]').forEach(function (b) { b.setAttribute('aria-pressed', 'false'); });
          btn.setAttribute('aria-pressed', 'true');
          apply();
        });
      });
    });
  }

  /* ---------------------------------------------------------- collection */

  /* Submits the sort form on change. The markup already works without this,
     via the <noscript> button, so this only removes a click. */
  function initCollection(root) {
    root.querySelectorAll('[data-sinai-sort]').forEach(function (select) {
      if (select.dataset.sinaiReady === '1') return;
      select.dataset.sinaiReady = '1';

      select.addEventListener('change', function () {
        var form = select.closest('[data-sinai-sortform]');
        if (form) form.submit();
      });
    });
  }

  /* ------------------------------------------------------------ cart icon */

  /* Horizon's declarative `on:click="#cart-drawer/toggle"` binding is only wired
     inside its own components, so a custom section has to call the drawer's
     public API itself. #cart-drawer is a <theme-drawer> exposing open/close/toggle. */
  function initCartToggle(root) {
    root.querySelectorAll('[data-sinai-cart-toggle]').forEach(function (btn) {
      if (btn.dataset.sinaiReady === '1') return;
      btn.dataset.sinaiReady = '1';

      btn.addEventListener('click', function () {
        var drawer = document.getElementById('cart-drawer');
        if (drawer && typeof drawer.toggle === 'function') {
          drawer.toggle();
          btn.setAttribute('aria-expanded', String(!!drawer.isOpen));
          return;
        }
        // No drawer on the page (or cart type is 'page') — fall back to /cart.
        window.location.href = (window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/') + 'cart';
      });
    });
  }

  /* ------------------------------------------------------------------ boot */

  /* Keep the header's cart badge honest.
     The badge is rendered by Liquid, so after an Add to Cart the item was in
     the cart but the icon still showed the count from page load - or nothing
     at all, if the cart had been empty. Horizon re-renders its own drawer, so
     the drawer's badge is the cheapest source of truth; /cart.js is the
     fallback for the paths that do not touch the drawer.
     Bound once per page, not per initAll call. */
  function initCartCount() {
    if (window.__sinaiCartCount) return;
    var host = document.querySelector(
      '.sinai-header__utils [aria-controls="cart-drawer"], .sinai-header__utils a[href$="/cart"]'
    );
    if (!host) return;
    window.__sinaiCartCount = true;

    var render = function (n) {
      var badge = host.querySelector('.sinai-header__count');
      if (!n) { if (badge) badge.parentNode.removeChild(badge); return; }
      if (!badge) {
        badge = document.createElement('span');
        badge.className = 'sinai-header__count';
        host.appendChild(badge);
      }
      if (badge.textContent !== String(n)) badge.textContent = n;
    };

    var pending = false;
    var sync = function () {
      if (pending) return;
      pending = true;
      /* Trailing read: the drawer mutates many times while it opens, and one
         value at the end is the only one worth showing. */
      setTimeout(function () {
        pending = false;
        var live = document.querySelector('#cart-drawer .theme-drawer__badge');
        var n = live ? parseInt(live.textContent.replace(/\D/g, ''), 10) : NaN;
        if (!isNaN(n)) { render(n); return; }
        var base = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        fetch(base + 'cart.js', { headers: { Accept: 'application/json' } })
          .then(function (r) { return r.json(); })
          .then(function (c) { render(c.item_count); })
          .catch(function () {});
      }, 250);
    };

    ['shopify:cart:lines-update', 'cart:update', 'cart:refresh'].forEach(function (name) {
      document.addEventListener(name, sync);
    });

    var drawer = document.getElementById('cart-drawer');
    if (drawer && window.MutationObserver) {
      /* sync only writes to the header, never to the drawer, so observing the
         drawer cannot feed itself. */
      new MutationObserver(sync).observe(drawer, { childList: true, subtree: true, characterData: true });
    }
  }

  /* Keep the inline menu from printing over the centred wordmark.
     A width breakpoint cannot do this: the menu's width depends on the labels
     the client types and the language Shopify serves, and in the theme editor
     the preview frame is much narrower than the window. So measure the menu
     once, then compare it against the room beside the wordmark. */
  function initHeaderFit() {
    if (window.__sinaiHeaderFit) return;
    var inner = document.querySelector('.sinai-header__inner');
    if (!inner) return;
    var nav = inner.querySelector('.sinai-header__nav');
    var brand = inner.querySelector('.sinai-header__brand');
    if (!nav || !brand) return;
    window.__sinaiHeaderFit = true;

    var applied = '';
    var cachedWidth = 0;
    var cachedKey = '';

    /* The menu's width changes at the 1400 and 1650 breakpoints, where the
       link size and the gap both step. Caching one figure for the whole page
       made the reading stale and sent 1440px desktops to the burger. Key the
       cache on the styles that actually move it, so it re-measures exactly
       once per breakpoint and not on every resize tick. */
    function naturalWidth() {
      var link = nav.querySelector('.sinai-header__navlink');
      var navCS = getComputedStyle(nav);
      var key =
        (link ? getComputedStyle(link).fontSize + getComputedStyle(link).letterSpacing : '') +
        '|' + navCS.columnGap + '|' + nav.children.length;
      if (key === cachedKey && cachedWidth) return cachedWidth;

      /* Read it out of flow and invisible, so taking the measurement cannot
         itself disturb the grid we are about to measure. */
      var prev = nav.getAttribute('style');
      nav.style.cssText =
        (prev || '') +
        ';position:absolute;visibility:hidden;display:flex;width:max-content;max-width:none;';
      var w = nav.getBoundingClientRect().width;
      if (prev === null) nav.removeAttribute('style');
      else nav.setAttribute('style', prev);

      cachedKey = key;
      cachedWidth = w;
      return w;
    }

    function fit() {
      var natural = naturalWidth();
      if (!natural) return;
      var cs = getComputedStyle(inner);
      var inline =
        inner.clientWidth -
        (parseFloat(cs.paddingLeft) || 0) -
        (parseFloat(cs.paddingRight) || 0);
      var gap = parseFloat(cs.columnGap) || 0;
      var logo = brand.getBoundingClientRect().width;
      /* The side tracks are 1fr each, so each gets half of what the wordmark
         and the two gaps leave behind. 24px of clearance keeps the menu from
         touching the logo rather than merely missing it. */
      var side = (inline - logo - gap * 2) / 2;
      var want = side >= natural + 24 ? 'inline' : 'drawer';
      /* Only write on a real change: the attribute alters the header's own
         box, and a ResizeObserver is watching it. */
      if (want !== applied) {
        applied = want;
        inner.setAttribute('data-nav', want);
      }
    }

    fit();
    window.addEventListener('resize', fit);
    /* The editor resizes the preview frame without resizing the window, and
       so does opening the cart drawer, so the window event is not enough. */
    if (window.ResizeObserver) new ResizeObserver(fit).observe(inner);
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(function () { cachedKey = ''; fit(); });
    }
  }

  /* Horizon opens the cart drawer non-modally and, above 990px, squeezes the
     page aside instead of locking it, so the site kept scrolling behind it.
     Mirror the lock the theme already uses for its own drawers. */
  function initCartScrollLock() {
    if (window.__sinaiCartLock) return;
    var drawer = document.getElementById('cart-drawer');
    if (!drawer || !window.MutationObserver) return;
    window.__sinaiCartLock = true;

    var dialog = drawer.matches('dialog') ? drawer : drawer.querySelector('dialog');
    if (!dialog) return;

    var sync = function () {
      var open = dialog.hasAttribute('open');
      if (open) document.documentElement.setAttribute('data-sinai-cartlock', '');
      else document.documentElement.removeAttribute('data-sinai-cartlock');
    };

    new MutationObserver(sync).observe(dialog, { attributes: true, attributeFilter: ['open'] });
    sync();
  }

  /* The white screen.
     At >=990px Horizon makes .page-wrapper the scroll container for the whole
     site - `height: 100dvh; overflow-y: auto`, with html and body set to
     `overflow: hidden`. So everything a visitor can see depends on that single
     box being exactly viewport-tall. If its height is ever stale or short - the
     theme editor resizing its preview frame, a bfcache restore, a dropped
     repaint - the site is clipped at that height and the rest of the window
     paints as bare white. That is the "everything disappeared" report: the page
     is still there, it is just being cut off.

     CSS cannot fix this, because the CSS is already right when it happens; the
     `dvh` value is simply stale. window.innerHeight is not a CSS length and
     never goes stale, so pin the height to it and re-pin on anything that could
     change it. The value written is exactly what the stylesheet intends. */
  function initViewportGuard() {
    if (window.__sinaiViewportGuard) return;
    var pw = document.querySelector('.page-wrapper');
    if (!pw) return;
    window.__sinaiViewportGuard = true;

    var squeeze = window.matchMedia('(min-width: 990px)');

    function check() {
      if (!squeeze.matches) {
        /* Below 990px the document scrolls natively and the wrapper is
           auto-height - leave it alone. */
        pw.style.removeProperty('height');
        return;
      }
      var want = window.innerHeight;
      if (!want) return;
      var px = want + 'px';
      if (pw.style.height !== px) pw.style.height = px;
    }

    window.addEventListener('resize', check);
    window.addEventListener('orientationchange', check);
    window.addEventListener('pageshow', check);
    document.addEventListener('visibilitychange', check);
    document.addEventListener('shopify:section:load', check);
    if (squeeze.addEventListener) squeeze.addEventListener('change', check);
    if (window.visualViewport) window.visualViewport.addEventListener('resize', check);
    check();
  }

  function initAll(root) {
    var scope = root || document;
    initDrawer(scope);
    initMarquee(scope);
    initCarousel(scope);
    initSlider(scope);
    initReveal(scope);
    initDropdowns(scope);
    initCartToggle(scope);
    initPdp(scope);
    initCollection(scope);
    initCartCount();
    initHeaderFit();
    initCartScrollLock();
    initViewportGuard();
  }

  /* Defensive reset.
     Two genuine ways this page can come back broken and "need a refresh":
     (1) the browser restores it from the back/forward cache with the drawer
         still open and <body> scroll-locked, and (2) a navigation is aborted
         mid-transition. Neither throws an error, so nothing self-heals.
     Clearing the transient UI state on every show makes both recoverable. */
  function resetTransientState() {
    document.documentElement.removeAttribute('data-sinai-cartlock');
    document.body.classList.remove('sinai-no-scroll');
    document.body.style.overflow = '';
    document.querySelectorAll('[data-sinai-drawer]').forEach(function (d) {
      d.setAttribute('data-open', 'false');
    });
    document.querySelectorAll('[data-sinai-hasmenu]').forEach(function (m) {
      m.setAttribute('data-open', 'false');
      var t = m.querySelector('[data-sinai-menutrigger]');
      if (t) t.setAttribute('aria-expanded', 'false');
    });
  }

  window.addEventListener('pageshow', function (e) {
    resetTransientState();
    // A bfcache restore skips DOMContentLoaded, so re-init too.
    if (e.persisted) initAll(document);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { resetTransientState(); initAll(document); });
  } else {
    resetTransientState();
    initAll(document);
  }

  // Shopify theme editor: sections are replaced in place, so re-init the
  // subtree rather than the whole document.
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
  document.addEventListener('shopify:section:select', function (e) { initAll(e.target); });

  // Re-measure marquees once webfonts land, otherwise the seam is computed
  // against fallback metrics and can leave a gap.
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(function () { initMarquee(document); });
  }
  window.addEventListener('resize', function () {
    clearTimeout(window.__sinaiRz);
    window.__sinaiRz = setTimeout(function () { initMarquee(document); }, 250);
  });
})();
